import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Package, Heading, MessageSquare, Calculator, TrendingDown, UserCheck, FileText } from 'lucide-react';
import { QuoteEditItem } from '../../../../hooks/useQuoteEdit';
import { TaxRate } from '../../../../utils/taxRatesAPI';
import { calculateVisualSubtotal, formatCurrency } from '../../../../utils/vatCalculations';

/** Feature flags pour affichage conditionnel des colonnes */
export interface EditableItemsFeatures {
  showReference?: boolean;   // referenceSKU
  showUnit?: boolean;        // unitOfMeasure
  showDiscount?: boolean;    // lineDiscount
  showSections?: boolean;    // sections (SECTION_TITLE / SUBTOTAL)
}

interface EditableItemsTableProps {
  items: QuoteEditItem[];
  onUpdateItem: (index: number, field: keyof QuoteEditItem, value: string | number | boolean | undefined) => void;
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  onAddFromCatalog?: () => void;
  onAddSectionTitle?: () => void;
  onAddComment?: () => void;
  onAddSubtotal?: () => void;
  tableStyle: 'striped' | 'bordered' | 'minimal';
  accentColor: string;
  showVat: boolean;
  // ✅ Les TaxRates sont fournis par le composant parent
  taxRates: TaxRate[];
  loadingTaxRates?: boolean;
  /** Feature flags — tout visible par défaut (backward compatible) */
  features?: EditableItemsFeatures;
}

/**
 * Tableau éditable des items d'un devis
 * Supporte 3 styles visuels tout en gardant la même fonctionnalité
 */
export const EditableItemsTable: React.FC<EditableItemsTableProps> = ({
  items,
  onUpdateItem,
  onAddItem,
  onRemoveItem,
  onAddFromCatalog,
  onAddSectionTitle,
  onAddComment,
  onAddSubtotal,
  tableStyle,
  accentColor,
  showVat,
  taxRates,
  loadingTaxRates = false,
  features,
}) => {
  const { t } = useTranslation();
  // ✅ Composant UI pur - pas de chargement de données ici

  // Feature flags — tout visible par défaut (backward compatible)
  const showReference = features?.showReference ?? true;
  const showUnit = features?.showUnit ?? true;
  const showDiscount = features?.showDiscount ?? true;
  const showSections = features?.showSections ?? true;

  // S1 — Cycle descriptionType: COMMERCIAL → TECHNICAL → BOTH → COMMERCIAL
  const cycleDescriptionType = (current?: 'COMMERCIAL' | 'TECHNICAL' | 'BOTH'): 'COMMERCIAL' | 'TECHNICAL' | 'BOTH' => {
    if (!current || current === 'COMMERCIAL') return 'TECHNICAL';
    if (current === 'TECHNICAL') return 'BOTH';
    return 'COMMERCIAL';
  };

  // Gestionnaire pour la sélection de TVA
  const handleTaxRateChange = (index: number, taxRateId: string) => {
    const selectedTaxRate = taxRates.find(tr => tr.id === taxRateId);
    if (selectedTaxRate) {
      // ✅ Mettre à jour les deux champs : taxRateId ET taxPercent
      onUpdateItem(index, 'taxRateId', selectedTaxRate.id);
      onUpdateItem(index, 'taxPercent', Number(selectedTaxRate.rate));
    }
  };

  // Styles selon le type de table
  const getTableClasses = () => {
    const base = 'w-full text-left text-sm';

    switch (tableStyle) {
      case 'striped':
        return `${base}`;
      case 'bordered':
        return `${base} border-2`;
      case 'minimal':
        return `${base}`;
      default:
        return base;
    }
  };

  const getRowClasses = (index: number) => {
    if (tableStyle === 'striped') {
      return index % 2 === 0 ? 'bg-gray-50' : 'bg-white';
    }
    if (tableStyle === 'bordered') {
      return 'border-b';
    }
    return '';
  };

  // Sous-total visuel — délègue à l'utilitaire partagé

  // Colonnes dynamiques: description(1) + quantity(1) + unitPrice(1) + totalHT(1) + actions(1) = 5 fixes
  // + reference(cond) + unit(cond) + discount(cond) + vat(cond)
  let colCount = 5;
  if (showReference) colCount++;
  if (showUnit) colCount++;
  if (showDiscount) colCount++;
  if (showVat) colCount++;

  return (
    <div className="mb-6">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className={getTableClasses()} style={tableStyle === 'bordered' ? { borderColor: accentColor } : {}}>
          <thead>
            <tr className="border-b-2" style={{ borderColor: accentColor }}>
              {showReference && <th className="px-3 py-3 font-semibold text-gray-700 w-20">{t('templates.common.reference', 'Réf.')}</th>}
              <th className="px-3 py-3 font-semibold text-gray-700">{t('templates.common.description')}</th>
              <th className="px-3 py-3 font-semibold text-gray-700 text-center w-20">{t('templates.common.quantity')}</th>
              {showUnit && <th className="px-3 py-3 font-semibold text-gray-700 text-center w-16">{t('templates.common.unit', 'Unité')}</th>}
              <th className="px-3 py-3 font-semibold text-gray-700 text-right w-28">{t('templates.common.unitPriceShort')}</th>
              {showDiscount && <th className="px-3 py-3 font-semibold text-gray-700 text-right w-24">{t('templates.common.discount', 'Remise')}</th>}
              {showVat && (
                <th className="px-3 py-3 font-semibold text-gray-700 text-right w-24">{t('templates.common.vat')}</th>
              )}
              <th className="px-3 py-3 font-semibold text-gray-700 text-right w-28">{t('templates.common.totalHT')}</th>
              <th className="px-3 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              // Ligne SECTION_TITLE
              if (item.lineType === 'SECTION_TITLE') {
                return (
                  <tr key={item.id} className="bg-gray-100">
                    <td colSpan={colCount - 1} className="px-3 py-3">
                      <input
                        type="text"
                        value={item.description || item.groupTitle || ''}
                        onChange={(e) => onUpdateItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2.5 bg-white/60 border border-gray-300 rounded-xl font-bold text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300"
                        placeholder={t('templates.common.sectionTitlePlaceholder', 'LOT 1 — Titre de section')}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => onRemoveItem(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('common.actions.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              }

              // Ligne COMMENT
              if (item.lineType === 'COMMENT') {
                return (
                  <tr key={item.id}>
                    <td colSpan={colCount - 1} className="px-3 py-3">
                      <textarea
                        value={item.description}
                        onChange={(e) => onUpdateItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2.5 bg-white/40 border border-white/40 rounded-xl italic text-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300 resize-y min-h-[40px]"
                        placeholder={t('templates.common.commentPlaceholder', 'Note ou commentaire...')}
                        rows={1}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => onRemoveItem(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('common.actions.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              }

              // Ligne SUBTOTAL
              if (item.lineType === 'SUBTOTAL') {
                const subtotalValue = calculateVisualSubtotal(items, index);
                return (
                  <tr key={item.id} className="border-t-2 border-gray-300">
                    <td colSpan={colCount - 2} className="px-3 py-3 text-right font-bold text-gray-900">
                      {t('templates.common.subtotal', 'Sous-total')}
                    </td>
                    <td className="px-3 py-3 text-right font-bold text-gray-900">
                      {formatCurrency(subtotalValue)}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => onRemoveItem(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('common.actions.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              }

              // Ligne ITEM (standard)
              return (
                <tr key={item.id} className={getRowClasses(index)}>
                  {/* Référence */}
                  {showReference && (
                    <td className="px-3 py-3 align-middle">
                      <input
                        type="text"
                        value={item.reference || ''}
                        onChange={(e) => onUpdateItem(index, 'reference', e.target.value)}
                        className="w-full px-2 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl text-xs focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                        placeholder={t('templates.common.referencePlaceholder', 'Réf.')}
                      />
                    </td>
                  )}

                  {/* Description */}
                  <td className="px-3 py-3 align-top">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => onUpdateItem(index, 'description', e.target.value)}
                      className="w-full px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                      placeholder={t('templates.common.descriptionPlaceholder')}
                    />
                    {/* S1 — Description type toggle + technical description */}
                    {item.technicalDescription && item.technicalDescription.trim() !== '' && (
                      <div className="mt-1.5 flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            onUpdateItem(index, 'descriptionType', cycleDescriptionType(item.descriptionType));
                          }}
                          className="inline-flex items-center gap-1 self-start px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-[10px] font-medium hover:bg-teal-100 transition-colors"
                          title={
                            item.descriptionType === 'TECHNICAL' ? t('templates.common.descriptionTechnical')
                              : item.descriptionType === 'BOTH' ? t('templates.common.descriptionBoth')
                              : t('templates.common.descriptionCommercial')
                          }
                        >
                          <FileText className="w-2.5 h-2.5" />
                          {item.descriptionType === 'TECHNICAL' ? 'T' : item.descriptionType === 'BOTH' ? 'C+T' : 'C'}
                        </button>
                        {(item.descriptionType === 'TECHNICAL' || item.descriptionType === 'BOTH') && (
                          <p className="text-xs italic text-gray-500 pl-1">{item.technicalDescription}</p>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Quantité */}
                  <td className="px-3 py-3 align-middle">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={item.quantity}
                      onChange={(e) => onUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl text-center focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                    />
                  </td>

                  {/* Unité */}
                  {showUnit && (
                    <td className="px-3 py-3 align-middle">
                      <input
                        type="text"
                        value={item.unit || ''}
                        onChange={(e) => onUpdateItem(index, 'unit', e.target.value)}
                        className="w-full px-2 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl text-center text-xs focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                        placeholder={t('templates.common.unitPlaceholder', 'u')}
                      />
                    </td>
                  )}

                  {/* Prix unitaire */}
                  <td className="px-3 py-3 align-middle">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => onUpdateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl text-right focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                    />
                    {item.tierApplied && (
                      <span className="inline-flex items-center gap-0.5 mt-1 px-1.5 py-0.5 bg-teal-50 text-teal-600 rounded text-[10px] font-medium">
                        <TrendingDown className="w-2.5 h-2.5" />
                        {t('products.tiers.badge', { ns: 'products', defaultValue: 'Dégressif' })}
                      </span>
                    )}
                    {item.clientPriceApplied && (
                      <span className="inline-flex items-center gap-0.5 mt-1 px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded text-[10px] font-medium">
                        <UserCheck className="w-2.5 h-2.5" />
                        {t('products.clientPricing.badge', { ns: 'products', defaultValue: 'Tarif client' })}
                      </span>
                    )}
                  </td>

                  {/* Remise */}
                  {showDiscount && (
                    <td className="px-3 py-3 align-middle">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.discountValue || ''}
                          onChange={(e) => onUpdateItem(index, 'discountValue', parseFloat(e.target.value) || 0)}
                          className="w-16 px-2 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl text-right text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                          placeholder="0"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            // P3-FIX: Cycle 3 états NONE → % → € → NONE
                            const next = !item.discountType || item.discountType === 'NONE'
                              ? 'PERCENTAGE'
                              : item.discountType === 'PERCENTAGE' ? 'FIXED' : 'NONE';
                            onUpdateItem(index, 'discountType', next);
                            if (next === 'NONE') onUpdateItem(index, 'discountValue', 0);
                          }}
                          className="px-2 py-2.5 bg-white/40 border border-white/40 rounded-xl text-xs font-medium text-gray-600 hover:bg-white/60 transition-all duration-300 min-w-[28px]"
                          title={
                            item.discountType === 'PERCENTAGE' ? t('templates.common.discountPercentage', '%')
                              : item.discountType === 'FIXED' ? t('templates.common.discountFixed', '€')
                              : t('templates.common.noDiscount', 'Aucune')
                          }
                        >
                          {item.discountType === 'FIXED' ? '€' : item.discountType === 'PERCENTAGE' ? '%' : '—'}
                        </button>
                      </div>
                    </td>
                  )}

                  {/* TVA */}
                  {showVat && (
                    <td className="px-3 py-3 align-middle">
                      <select
                        value={item.taxRateId || ''}
                        onChange={(e) => handleTaxRateChange(index, e.target.value)}
                        className="w-full px-2 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                        disabled={loadingTaxRates}
                      >
                        {!item.taxRateId && <option value="">{t('templates.common.selectPlaceholder')}</option>}
                        {taxRates.map((taxRate) => (
                          <option key={taxRate.id} value={taxRate.id}>
                            {taxRate.name} ({Number(taxRate.rate)}%)
                          </option>
                        ))}
                      </select>
                    </td>
                  )}

                  {/* Total */}
                  <td className="px-3 py-3 text-right font-semibold align-middle">
                    {item.discountAmount && item.discountAmount > 0 ? (
                      <div>
                        <span className="line-through text-gray-400 text-xs block">{formatCurrency(item.quantity * item.unitPrice)}</span>
                        <span>{formatCurrency(item.total)}</span>
                      </div>
                    ) : (
                      <span>{formatCurrency(item.total)}</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-3 align-middle">
                    <button
                      onClick={() => onRemoveItem(index)}
                      disabled={items.filter(i => !i.lineType || i.lineType === 'ITEM').length === 1 && (!item.lineType || item.lineType === 'ITEM')}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('common.actions.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {items.map((item, index) => {
          // Mobile: SECTION_TITLE
          if (item.lineType === 'SECTION_TITLE') {
            return (
              <div key={item.id} className="bg-gray-100 rounded-xl p-3 flex items-center justify-between">
                <input
                  type="text"
                  value={item.description || item.groupTitle || ''}
                  onChange={(e) => onUpdateItem(index, 'description', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/60 border border-gray-300 rounded-lg font-bold text-gray-900 focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder={t('templates.common.sectionTitlePlaceholder', 'LOT 1 — Titre de section')}
                />
                <button onClick={() => onRemoveItem(index)} className="ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          }

          // Mobile: COMMENT
          if (item.lineType === 'COMMENT') {
            return (
              <div key={item.id} className="border-l-4 border-gray-300 pl-3 py-2 flex items-start gap-2">
                <textarea
                  value={item.description}
                  onChange={(e) => onUpdateItem(index, 'description', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/40 border border-white/40 rounded-lg italic text-gray-500 text-sm focus:ring-2 focus:ring-teal-500 resize-y min-h-[40px]"
                  placeholder={t('templates.common.commentPlaceholder', 'Note ou commentaire...')}
                  rows={1}
                />
                <button onClick={() => onRemoveItem(index)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          }

          // Mobile: SUBTOTAL
          if (item.lineType === 'SUBTOTAL') {
            const subtotalValue = calculateVisualSubtotal(items, index);
            return (
              <div key={item.id} className="border-t-2 border-gray-300 pt-2 flex justify-between items-center">
                <span className="font-bold text-gray-900">{t('templates.common.subtotal', 'Sous-total')}</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900">{formatCurrency(subtotalValue)}</span>
                  <button onClick={() => onRemoveItem(index)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          }

          // Mobile: ITEM (standard)
          return (
            <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3">
              {/* Description */}
              <input
                type="text"
                value={item.description}
                onChange={(e) => onUpdateItem(index, 'description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-teal-500"
                placeholder={t('templates.common.descriptionPlaceholder')}
              />
              {/* S1 — Description type toggle (mobile) */}
              {item.technicalDescription && item.technicalDescription.trim() !== '' && (
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => onUpdateItem(index, 'descriptionType', cycleDescriptionType(item.descriptionType))}
                    className="inline-flex items-center gap-1 self-start px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-[10px] font-medium hover:bg-teal-100 transition-colors"
                  >
                    <FileText className="w-2.5 h-2.5" />
                    {item.descriptionType === 'TECHNICAL' ? 'T' : item.descriptionType === 'BOTH' ? 'C+T' : 'C'}
                  </button>
                  {(item.descriptionType === 'TECHNICAL' || item.descriptionType === 'BOTH') && (
                    <p className="text-xs italic text-gray-500 pl-1">{item.technicalDescription}</p>
                  )}
                </div>
              )}

              {/* Ref + Unit */}
              {(showReference || showUnit) && (
                <div className={`grid gap-2 ${showReference && showUnit ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {showReference && (
                    <input
                      type="text"
                      value={item.reference || ''}
                      onChange={(e) => onUpdateItem(index, 'reference', e.target.value)}
                      className="px-2 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-teal-500"
                      placeholder={t('templates.common.referencePlaceholder', 'Réf.')}
                    />
                  )}
                  {showUnit && (
                    <input
                      type="text"
                      value={item.unit || ''}
                      onChange={(e) => onUpdateItem(index, 'unit', e.target.value)}
                      className="px-2 py-2 border border-gray-200 rounded-lg text-xs text-center focus:ring-2 focus:ring-teal-500"
                      placeholder={t('templates.common.unitPlaceholder', 'u')}
                    />
                  )}
                </div>
              )}

              {/* Qty + Unit Price */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">{t('templates.common.quantity')}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={item.quantity}
                    onChange={(e) => onUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">{t('templates.common.unitPriceShort')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => onUpdateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm text-right focus:ring-2 focus:ring-teal-500"
                  />
                  {item.tierApplied && (
                    <span className="inline-flex items-center gap-0.5 mt-1 px-1.5 py-0.5 bg-teal-50 text-teal-600 rounded text-[10px] font-medium">
                      <TrendingDown className="w-2.5 h-2.5" />
                      {t('products.tiers.badge', { ns: 'products', defaultValue: 'Dégressif' })}
                    </span>
                  )}
                  {item.clientPriceApplied && (
                    <span className="inline-flex items-center gap-0.5 mt-1 px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded text-[10px] font-medium">
                      <UserCheck className="w-2.5 h-2.5" />
                      {t('products.clientPricing.badge', { ns: 'products', defaultValue: 'Tarif client' })}
                    </span>
                  )}
                </div>
              </div>

              {/* Discount + TVA */}
              {(showDiscount || showVat) && (
                <div className={`grid gap-2 ${showDiscount && showVat ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {showDiscount && (
                    <div className="flex items-end gap-1">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-1 block">{t('templates.common.discount', 'Remise')}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.discountValue || ''}
                          onChange={(e) => onUpdateItem(index, 'discountValue', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm text-right focus:ring-2 focus:ring-teal-500"
                          placeholder="0"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          // P3-FIX: Cycle 3 états NONE → % → € → NONE (mobile)
                          const next = !item.discountType || item.discountType === 'NONE'
                            ? 'PERCENTAGE'
                            : item.discountType === 'PERCENTAGE' ? 'FIXED' : 'NONE';
                          onUpdateItem(index, 'discountType', next);
                          if (next === 'NONE') onUpdateItem(index, 'discountValue', 0);
                        }}
                        className="px-2 py-2 bg-gray-100 border border-gray-200 rounded-lg text-xs font-medium text-gray-600"
                      >
                        {item.discountType === 'FIXED' ? '€' : item.discountType === 'PERCENTAGE' ? '%' : '—'}
                      </button>
                    </div>
                  )}
                  {showVat && (
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">{t('templates.common.vat')}</label>
                      <select
                        value={item.taxRateId || ''}
                        onChange={(e) => handleTaxRateChange(index, e.target.value)}
                        className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                        disabled={loadingTaxRates}
                      >
                        {!item.taxRateId && <option value="">{t('templates.common.selectPlaceholder')}</option>}
                        {taxRates.map((taxRate) => (
                          <option key={taxRate.id} value={taxRate.id}>
                            {Number(taxRate.rate)}%
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Total + Delete */}
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="text-sm font-bold" style={{ color: accentColor }}>
                  {t('templates.common.totalHT')}: {formatCurrency(item.total)}
                </span>
                <button
                  onClick={() => onRemoveItem(index)}
                  disabled={items.filter(i => !i.lineType || i.lineType === 'ITEM').length === 1 && (!item.lineType || item.lineType === 'ITEM')}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Boutons d'action - V2 Design */}
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          onClick={onAddItem}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl transition-all duration-300 shadow-lg shadow-teal-500/20 hover:shadow-xl hover:-translate-y-0.5 font-medium text-sm"
        >
          <Plus className="w-4 h-4" />
          {t('templates.common.addLine')}
        </button>

        {onAddFromCatalog && (
          <button
            onClick={onAddFromCatalog}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 hover:shadow-md font-medium text-sm"
          >
            <Package className="w-4 h-4" />
            {t('templates.common.fromCatalog')}
          </button>
        )}

        {showSections && onAddSectionTitle && (
          <button
            onClick={onAddSectionTitle}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 hover:shadow-md font-medium text-sm"
          >
            <Heading className="w-4 h-4" />
            {t('templates.common.addSectionTitle', 'Titre de section')}
          </button>
        )}

        {onAddComment && (
          <button
            onClick={onAddComment}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 hover:shadow-md font-medium text-sm"
          >
            <MessageSquare className="w-4 h-4" />
            {t('templates.common.addComment', 'Commentaire')}
          </button>
        )}

        {showSections && onAddSubtotal && (
          <button
            onClick={onAddSubtotal}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 hover:shadow-md font-medium text-sm"
          >
            <Calculator className="w-4 h-4" />
            {t('templates.common.addSubtotal', 'Sous-total')}
          </button>
        )}
      </div>
    </div>
  );
};
